import {
  ExportBeneficiarySearchParams,
  MAX_BENEFICIARIES_EXPORTED_FILENAME_LENGTH,
} from '@/modules/beneficiary/entities';
import { BeneficiaryService } from '@/modules/beneficiary/services';
import useCompany from '@/shared/hooks/useCompany/useCompany';
import { getMessageError } from '@/utils/getMessageError';
import { safeJsonParse } from '@/utils/validations/safeJsonParse';
import { format } from 'date-fns';
import { get } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

const beneficiaryService = new BeneficiaryService();

const useExportBeneficiriesModal = () => {
  const { company } = useCompany();
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const resetAll = () => {
    setLoading(false);
    setSuccess(false);
    setErrorMessage(null);
  };

  const downloadFile = useCallback(
    (response: Blob) => {
      const formatedDate = `-${format(new Date(), 'dd-MM-yyyy-HH-mm-ss')}`;
      const filenamePrefix = 'beneficiarios-';
      const fixedNameSize = filenamePrefix.length + formatedDate.length;
      const limitedCompanyName = company.name.slice(
        0,
        MAX_BENEFICIARIES_EXPORTED_FILENAME_LENGTH - fixedNameSize,
      );
      const filename = `${filenamePrefix}${limitedCompanyName}${formatedDate}.xlsx`;

      const url = window.URL.createObjectURL(new Blob([response]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
    [company.name],
  );

  const getExportedData = useCallback(
    async (filters: ExportBeneficiarySearchParams) => {
      if (!filters.companyId) {
        return;
      }
      setLoading(true);
      setSuccess(false);
      setErrorMessage(null);

      try {
        const response = await beneficiaryService.getExportedBeneficiaries(filters);
        downloadFile(response);
        setSuccess(true);
      } catch (e) {
        const reader = new FileReader();
        reader.onload = function () {
          setErrorMessage(
            getMessageError({
              error: safeJsonParse(String(reader.result)),
              pathMessageRequest: 'message',
              messageDefault:
                'Um erro aconteceu e não conseguimos processar sua solicitação com sucesso. ' +
                'Caso queira tentar novamente utilize a ação abaixo.',
              messageErrorByMessageErrorRequest: {
                ['Beneficiary count exceeds the .+ limit. Please refine your filters to narrow the results.']:
                  'O volume de vidas selecionado para exportação é muito alto e pode ' +
                  'comprometer a performance. Para continuar, reduza a quantidade de registros ' +
                  'exportados utilizando filtros ou selecionando menos empresas e tente novamente.',
              },
            }),
          );
        };

        const messageErrorRequest = get(e, 'response.data') as unknown as Blob;
        reader.readAsText(messageErrorRequest);
      } finally {
        setLoading(false);
      }
    },
    [downloadFile],
  );

  const disableCloseButton = useMemo(() => {
    return loading && (!errorMessage || !success);
  }, [errorMessage, loading, success]);

  return {
    loading,
    errorMessage,
    getExportedData,
    success,
    disableCloseButton,
    resetAll,
  };
};

export default useExportBeneficiriesModal;
